import React, { useMemo, useContext, memo } from "react";
import fp from "lodash/fp";
import { Row, Col, Form } from "react-bootstrap";
import Creatable from "react-select/creatable";

import { Skeleton, SelectAsync, RequiredMarkContext } from "components";
import { ApplicationEntryProgressContext } from "components/contexts";
import ReadOnlyInput from "components/formFields/readOnlyInput";
import { StartTypingMessage } from "containers/systemSetup/components/formComponents";
import { DebounceTimeout } from "globalConstants";

import { selectStyles } from "../selectStyles";

import classes from "../selectAsync.module.scss";

const SelectAsyncForBankRouting = ({
	input,
	meta: { touched, error, warning },
	disabled,
	loadOptions,
	findOption,
	onChangeCb,
	blurLoading,
	cacheOptions = true,
	hideRequiredMark, //works only with RequiredMarkContext
	readOnly,
	label = "Routing",
	...props
}) => {
	const showRequiredMark =
		useContext(RequiredMarkContext) && !hideRequiredMark && !disabled;
	const { triggerSave } = useContext(ApplicationEntryProgressContext);

	const checkSpanRequired = showRequiredMark ? (
		<span className={classes.spanRequired}>*</span>
	) : null;

	const showError = touched && !disabled && !blurLoading && error;

	const errorText = showError && (
		<p style={selectStyles.selectAsyncErrorMessage()}>{error}</p>
	);

	const showWarning = !disabled && !blurLoading && warning;

	const warningText = showWarning && (
		<p style={selectStyles.selectAsyncWarningMessage()}>{warning}</p>
	);

	const formatCreateLabel = value => `Enter "${value}"`;

	const filterOption = (option, query) => {
		if (option.data.__isNew__) return true;

		return (
			option.data.value &&
			option.data.value.toLowerCase().includes(query.toLowerCase())
		);
	};

	const handleChange = ({ value }) => {
		input.onChange(value);
		if (onChangeCb) onChangeCb(value);
		triggerSave?.();
	};

	const handleBlur = ({ value }) => {
		input.onBlur(value);
	};

	const SelectWithError = (
		<>
			<SelectAsync
				{...props}
				SelectComponent={Creatable}
				value={findOption(input.value)}
				onChange={handleChange}
				onBlur={handleBlur}
				formatCreateLabel={formatCreateLabel}
				filterOption={filterOption}
				debounceTimeout={DebounceTimeout}
				cacheOptions={cacheOptions}
				loadOptions={loadOptions}
				additional={{ page: 0 }}
				maxMenuHeight={160}
				noOptionsMessage={StartTypingMessage}
				hasError={touched && error}
				hasWarning={warning}
				key={`banks-select-${input.value}`}
			/>
			{errorText || warningText}
		</>
	);

	const SelectWithLoader = useMemo(
		() =>
			fp.cond([
				[() => blurLoading, () => <Skeleton />],
				[
					() => readOnly,
					({ input }) => (
						<ReadOnlyInput name={input.name} value={input.value} />
					),
				],
				[fp.T, ({ component }) => component],
			]),
		[blurLoading, readOnly],
	);

	const isDisabledCss = readOnly && disabled && { opacity: "0.1" };

	return (
		<Row style={{ marginBottom: 22, ...isDisabledCss }}>
			<Form.Label column sm={6}>
				{label}
				{checkSpanRequired}
			</Form.Label>
			<Col sm={6}>
				<SelectWithLoader input={input} component={SelectWithError} />
			</Col>
		</Row>
	);
};

export default memo(SelectAsyncForBankRouting);
